import { useEffect } from 'react'

import {
  Modal,
  Group,
  Divider,
  Tooltip,
  useModalStackContext,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import dayjs from 'dayjs'
import {
  Mail,
  Phone,
  Printer,
  StickyNote,
  FileQuestionMark,
} from 'lucide-react'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import {
  FormTextareaField,
  FormDateField,
  FormPhoneField,
  FormContactField,
  FormTextField,
  FormSelectField,
} from 'src/components/Overlays/EmploymentDetailsDrawer/components/FormComponents'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { VoEAuditTrailType } from 'src/graphql/types/verificationOfEmployments'
import { CREATE_VOE_AUDIT_TRAIL } from 'src/graphql/voe.graphql'
import { formatSnakeValue } from 'src/lib/formatters'
import { removeCountryCode, unformatPhoneNumber } from 'src/lib/phone.utils'

type ContactModalVoE = {
  contactName?: string | null
  email?: string | null
  phoneNumber?: string | null
  faxNumber?: string | null
}

type ContactSelection = {
  contactName?: string | null
  phoneNumber?: string | null
  phone?: string | null
  email?: string | null
  fax?: string | null
  faxNumber?: string | null
}

export const getContactModalInitialValues = (
  voe?: ContactModalVoE,
  contactMethod = VoEAuditTrailType.CALL
) => ({
  contactMethod,
  contactName: voe?.contactName || '',
  contactDate: new Date(),
  email: voe?.email || '',
  phone: voe?.phoneNumber || '',
  fax: voe?.faxNumber || '',
  message: undefined,
})

export const getContactSelectionValues = (selected?: ContactSelection) => ({
  contactName: selected?.contactName || '',
  phone: selected?.phoneNumber || selected?.phone || '',
  email: selected?.email || '',
  fax: selected?.fax || selected?.faxNumber || '',
})

const STACK_ID = 'voe-contact-modal'

const ContactModal = ({ voe, isOpen, onRender, onClose, onContactSuccess }) => {
  const stackCtx = useModalStackContext()

  // Mantine gap: Modal's stack-register useEffect has no cleanup, so
  // unmounting while opened leaks the stackId in Modal.Stack. The save flow
  // unmounts this modal via parent state cascade (VoETab clears
  // isContactModalOpen and opens a follow-up DnPrompt in the same React batch),
  // which left a stale scrim grayed out over the next prompt. Mirrors DeleteModal.
  useEffect(() => {
    return () => {
      if (stackCtx) {
        stackCtx.removeModal(STACK_ID)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //This should reflect the VoEAuditTrailType enum in the api
  const contactTypeData = Object.values(VoEAuditTrailType).map((type) => ({
    label: type === 'ATS' ? 'Other (e.g. ATS)' : formatSnakeValue(type),
    value: type,
  }))

  const schema = z.object({
    contactMethod: z.string({
      invalid_type_error: 'Contact method is required',
      required_error: 'Contact method is required',
    }),
    contactDate: z.date(),
    email: z
      .string()
      .email({ message: 'Invalid email' })
      .optional()
      .or(z.literal('')),
    phone: z
      .string()
      .refine(
        (data) =>
          unformatPhoneNumber(removeCountryCode(data)).length === 10 ||
          unformatPhoneNumber(removeCountryCode(data)).length === 0,
        {
          message: 'Phone number should have 10 digits',
        }
      )
      .optional(),
    fax: z
      .string()
      .refine(
        (data) =>
          unformatPhoneNumber(removeCountryCode(data)).length === 10 ||
          unformatPhoneNumber(removeCountryCode(data)).length === 0,
        {
          message: 'Fax number should have 10 digits',
        }
      )
      .optional(),
    message: z.string().optional(),
  })

  const form = useForm({
    initialValues: getContactModalInitialValues(voe, undefined),
    validate: zodResolver(schema),
  })

  const [createVoEAuditTrail, { loading }] = useMutation(
    CREATE_VOE_AUDIT_TRAIL,
    {
      refetchQueries: onRender,
      awaitRefetchQueries: true,
      onCompleted: () => {
        toast('Contact attempt saved successfully', 'success')
        onClose()
        if (onContactSuccess) {
          // Pass the contact information that was just submitted
          onContactSuccess({
            contactName: form.values.contactName,
            email: form.values.email,
            phone: form.values.phone,
            fax: form.values.fax,
            contactMethod: form.values.contactMethod,
          })
        }
      },
      onError: () => {
        toast('Unable to create contact attempt, please try again.', 'error')
      },
    }
  )

  const resetValues = () => {
    form.setValues(getContactModalInitialValues(voe))
  }

  useEffect(() => {
    if (isOpen) {
      resetValues()
    }
  }, [isOpen, voe?.voeId])

  const handleSubmit = (values) => {
    const input = {
      title: `${formatSnakeValue(values.contactMethod)} Attempt`,
      message:
        values.message !== undefined && values.message !== ''
          ? values.message
          : `Contact attempt made via ${formatSnakeValue(
              values.contactMethod
            )} on ${dayjs(values.contactDate).format(
              'MM/DD/YYYY [at] h:mm A'
            )}`,
      type: form.values.contactMethod,
      contactDate: values.contactDate,
      contactName: values.contactName,
      ...(values.email !== undefined &&
        values.email !== '' && { email: values.email }),
      ...(values.fax !== undefined && values.fax !== '' && { fax: values.fax }),
      ...(values.phone !== undefined &&
        values.phone !== '' && { phone: values.phone }),
    }

    createVoEAuditTrail({
      variables: {
        voeId: voe.voeId,
        input,
      },
    })
  }

  const handleContactSelected = (selected: ContactSelection) => {
    if (!selected) return
    const contactSelectionValues = getContactSelectionValues(selected)
    // Only overwrite fields the selected contact actually provides — otherwise
    // preserve any prefilled values (e.g. FMCSA results lack fax numbers, and
    // partial contacts may be missing phone or email).
    if (contactSelectionValues.contactName) {
      form.setFieldValue('contactName', contactSelectionValues.contactName)
    }
    if (contactSelectionValues.phone) {
      form.setFieldValue('phone', contactSelectionValues.phone)
    }
    if (contactSelectionValues.email) {
      form.setFieldValue('email', contactSelectionValues.email)
    }
    if (contactSelectionValues.fax) {
      form.setFieldValue('fax', contactSelectionValues.fax)
    }
  }

  const isFormInvalid = () =>
    (form.values.contactMethod === VoEAuditTrailType.EMAIL &&
      !form.values.email) ||
    (form.values.contactMethod === VoEAuditTrailType.CALL &&
      !form.values.phone) ||
    (form.values.contactMethod === VoEAuditTrailType.FAX && !form.values.fax)

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      classNames={{ title: 'font-medium' }}
      title={`Log contact attempt for ${voe.companyName}`}
      centered
      stackId={STACK_ID}
    >
      <form
        onSubmit={form.onSubmit((values) => {
          if (!isFormInvalid()) {
            handleSubmit(values)
          }
        })}
        className="flex h-full flex-1 flex-col gap-4"
      >
        <div className="mt-4 flex h-full w-full flex-col gap-4">
          <div className="flex flex-col gap-2">
            <FormSelectField
              label="Contact Method"
              editing={true}
              required={true}
              formProps={{
                ...form.getInputProps('contactMethod'),
                data: contactTypeData,
              }}
            />
            <FormDateField
              label="Date of Contact"
              required={true}
              editing={true}
              formProps={form.getInputProps('contactDate')}
            />

            <FormTextareaField
              label="Notes"
              editing={true}
              formProps={form.getInputProps('message')}
            />
          </div>
          <Divider variant="dashed" />

          <div className="flex flex-col gap-2">
            <FormContactField
              dotNumber={voe.dotNumber}
              label="Contact Name"
              editing={true}
              formProps={form.getInputProps('contactName')}
              onSelect={handleContactSelected}
            />
            <FormTextField
              label="Email"
              required={form.values.contactMethod === VoEAuditTrailType.EMAIL}
              editing={true}
              formProps={form.getInputProps('email')}
            />
            <FormPhoneField
              label="Phone"
              required={form.values.contactMethod === VoEAuditTrailType.CALL}
              editing={true}
              formProps={form.getInputProps('phone')}
            />
            <FormPhoneField
              label="Fax Number"
              required={form.values.contactMethod === VoEAuditTrailType.FAX}
              editing={true}
              formProps={form.getInputProps('fax')}
            />
          </div>
        </div>

        <Divider variant="dashed" className="mt-auto" />

        <Group
          justify="flex-end"
          className="flex flex-row items-center justify-end gap-4"
        >
          <DNButton variant="outline" onClick={onClose} className="w-24">
            Cancel
          </DNButton>
          <Tooltip
            label={
              isFormInvalid()
                ? 'Please fix form errors before saving'
                : 'Save changes'
            }
          >
            <DNButton
              variant="primary"
              type="submit"
              disabled={isFormInvalid()}
              loading={loading}
              className="w-24"
            >
              Save
            </DNButton>
          </Tooltip>
        </Group>
      </form>
    </Modal>
  )
}

export default ContactModal
